import React, { useEffect, useRef, useState } from "react";
import { RouteComponentProps } from "react-router";
import { useHistory } from "react-router-dom";
import { Spinner } from "reactstrap";
import { Trans } from "@lingui/react";
import Button from "./Button";

import request from "@/utils/request";
import api from "@/utils/api";
import { Booking, StripeUiMode } from "@/types";
import {
  getBrowserFlags,
  getCountryCode,
  getErrorMessageFromApiError,
  readConfigurationProperty,
} from "@/utils/common";
import { useAppDispatch, useAppSelector } from "@/hooks/hook";
import { setAccountId, clearAccountId } from "@/reducers/stripeSlice";
import { PaymentProviderIds } from "@/constants";
import StripeCheckout from "@/components/StripeCheckout";

interface Props {
  bookingId: string;
}

interface CheckoutResponse {
  Url?: string;
  ClientSecret?: string;
  StripeAccount?: string;
}

export const Payment: React.FC<Props> = ({ bookingId }) => {
  const [checkout, setCheckout] = useState<CheckoutResponse | undefined>();
  const [booking, setBooking] = useState<Booking | undefined>();
  const [error, setError] = useState<string | undefined>();
  const dispatch = useAppDispatch();
  const { push } = useHistory();
  const customerEmail = useAppSelector((s) => s.customer.data?.customer.Email);
  const configuration = useAppSelector((s) => s.configuration.data);
  const customerEmailRef = useRef(customerEmail);

  useEffect(() => {
    customerEmailRef.current = customerEmail;
  }, [customerEmail]);

  const payWithQvickly = booking?.Company.PaymentProviderId === PaymentProviderIds.QVICKLY;
  const payWithStripe = booking?.Company.PaymentProviderId === PaymentProviderIds.STRIPE;

  useEffect(() => {
    return () => {
      dispatch(clearAccountId());
    };
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        if (!configuration) {
          throw new Error("No application configuration found.");
        }

        dispatch(clearAccountId());

        const bookingResponse = await request(api.fetchBookings({ Id: bookingId }));
        if (cancelled) return;
        const selectedBooking = bookingResponse.data.Results?.[0] as Booking | undefined;

        if (!selectedBooking) {
          throw new Error("No booking found.");
        }
        const checkoutCustomerEmail =
          customerEmailRef.current || selectedBooking.Customer?.Email || "";

        setBooking(selectedBooking);

        const paymentProviderId = selectedBooking.Company.PaymentProviderId;
        if (
          paymentProviderId !== PaymentProviderIds.QVICKLY &&
          paymentProviderId !== PaymentProviderIds.STRIPE
        ) {
          throw new Error("Unsupported payment provider.");
        }

        const { iOSSafari } = getBrowserFlags();
        // iOS Safari ITP blocks Stripe embedded checkout cookies in nested iframes → force hosted.
        const uiMode: StripeUiMode =
          !configuration.paymentButton && !iOSSafari ? "embedded" : "hosted";

        const checkoutResponse = await request(
          api.createCheckout(
            {
              Id: selectedBooking.Id,
              CompanyId: selectedBooking.Company.Id,
              PaymentProviderId: paymentProviderId,
              CountryCode: getCountryCode(readConfigurationProperty("language")),
              Articles: [
                {
                  ArticleTypeId: 1,
                  InternalReferenceId: selectedBooking.Id,
                  Quantity: 1,
                },
              ],
              UiMode: uiMode,
              customerEmail: checkoutCustomerEmail,
            },
            configuration
          )
        );
        if (cancelled) return;

        const checkoutData = checkoutResponse.data as CheckoutResponse;
        if (checkoutData.StripeAccount) {
          dispatch(setAccountId(checkoutData.StripeAccount));
        }
        setCheckout(checkoutData);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(getErrorMessageFromApiError(err));
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [bookingId, configuration, dispatch]);

  useEffect(() => {
    if (payWithStripe && checkout?.Url && !checkout.ClientSecret) {
      try {
        if (window.top) {
          window.top.location.href = checkout.Url;
        }
      } catch {
        window.open(checkout.Url, "_top");
      }
    }
  }, [checkout, payWithStripe]);

  return (
    <div className="Payment">
      {checkout ? (
        <>
          {payWithStripe && checkout.ClientSecret ? (
            <StripeCheckout clientSecret={checkout.ClientSecret} />
          ) : null}
          {payWithQvickly && checkout.Url ? (
            <iframe
              src={checkout.Url}
              width="100%"
              height="810px"
              frameBorder="0"
              data-testid="billmateContainer"
              title="Billmate"
            />
          ) : null}
        </>
      ) : error ? (
        <div className="container p-2 text-center">
          <p>
            <Trans id="payment.followingErrorHasOccured" values={{ bookingId }} />
          </p>
          <p className="text-danger">{error}</p>
          <Button
            primary
            onClick={() => {
              push("/");
              window.location.reload();
            }}
          >
            <Trans id="reload" />
          </Button>
        </div>
      ) : (
        <div className="Payment__loading">
          <Spinner color="secondary" />
        </div>
      )}
    </div>
  );
};

interface PageProps extends RouteComponentProps<{ bookingId: string }> {}

const PaymentPage: React.FC<PageProps> = (props) => (
  <Payment bookingId={props.match.params.bookingId} />
);

export default PaymentPage;
